import React from "react";
import {
  compareSeriesLegendEntries,
  formatIsoForDayLabel,
} from "./HourlyDaySeriesTooltip";

export type HourlyVolumeLegendProps = {
  payload?: any[];
  selDates: string[];
  onToggle: (date: string) => void;
  fullscreen?: boolean;
  onSelectAll?: () => void;
  onClearSelection?: () => void;
};

function HourlyVolumeLegendInner({
  payload,
  selDates,
  onToggle,
  fullscreen = false,
  onSelectAll,
  onClearSelection,
}: HourlyVolumeLegendProps) {
  const fs = Boolean(fullscreen);
  if (!payload?.length) return null;

  const sortedPayload = [...payload].sort(compareSeriesLegendEntries);

  const chip = (entry: any, index: number) => {
    const key = String(entry.dataKey ?? entry.value ?? "");
    const isSelected = selDates.includes(key);
    const labelText =
      entry.value != null && String(entry.value).trim() !== ""
        ? String(entry.value)
        : formatIsoForDayLabel(key);
    return (
      <button
        key={key || `chip-${index}`}
        type="button"
        onClick={() => onToggle(key)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: fs ? "6px" : "10px",
          padding: fs ? "6px 8px" : "10px 16px",
          backgroundColor: isSelected ? "#ffffff" : "#e2e8f0",
          border: isSelected
            ? `2px solid ${entry.color}`
            : "2px solid transparent",
          borderRadius: fs ? "8px" : "10px",
          cursor: "pointer",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          fontSize: fs ? "11px" : "13px",
          fontWeight: 600,
          color: isSelected ? "#1e293b" : "#94a3b8",
          boxShadow: isSelected ? `0 2px 8px ${entry.color}20` : "none",
          minWidth: 0,
          textAlign: "left" as const,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <span
          style={{
            width: fs ? "10px" : "16px",
            height: fs ? "10px" : "16px",
            borderRadius: "3px",
            flexShrink: 0,
            backgroundColor: isSelected ? entry.color : "#94a3b8",
            boxShadow: isSelected ? `0 0 6px ${entry.color}45` : "none",
          }}
        />
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {labelText}
        </span>
      </button>
    );
  };

  if (!fs) {
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          justifyContent: "center",
          padding: "20px",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          marginTop: "20px",
          border: "1px solid #e2e8f0",
        }}
      >
        {sortedPayload.map((entry, index) => chip(entry, index))}
      </div>
    );
  }

  const miniBtn: React.CSSProperties = {
    fontSize: "11px",
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: "6px",
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#475569",
    cursor: "pointer",
  };

  return (
    <div
      style={{
        marginTop: "12px",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        backgroundColor: "#f8fafc",
        width: "98%",
        overflow: "hidden",
        position: "relative",
        zIndex: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "8px",
          padding: "10px 12px",
          background: "#eef2f7",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <span style={{ fontSize: "12px", fontWeight: 600, color: "#334155" }}>
          Dates shown: {selDates.length} / {sortedPayload.length}
        </span>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            type="button"
            style={miniBtn}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSelectAll?.();
            }}
          >
            Select all
          </button>
          <button
            type="button"
            style={miniBtn}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClearSelection?.();
            }}
          >
            Clear all
          </button>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
          gap: "6px",
          padding: "10px",
          maxHeight: "min(200px, 30vh)",
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        {sortedPayload.map((entry, index) => chip(entry, index))}
      </div>
    </div>
  );
}

export const HourlyVolumeLegend = React.memo(HourlyVolumeLegendInner);
